import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import { FiInfo, FiPlus, FiUser } from 'react-icons/fi';
import { auth, db } from '../../firebase';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import styled, { css } from 'styled-components';
import {
  selectActiveSubscription,
  selectIsLoading,
  selectUser,
  selectTilesDetails,
  selectRestrictedDataError,
  selectOpenAiTokens,
  fetchData,
} from '../../../../mainApp/features/DataSlice/dataSlice';
import LoadingPage from '../../TechnicalTabs/LoadingPage';
import ErrorPage from '../../TechnicalTabs/ErrorPage';
import { onAuthStateChanged } from 'firebase/auth';
import { handleUnauthorizedAccess } from '../../Utility/handleUnauthorizedAccess';
import {
  SettingsWrapper,
  LeftMenu,
  MenuItem,
  ContentArea,
  ContentHeader,
  ContentSection,
  SectionTitle,
  SectionDescription,
  ManageButton,
  PlanInfo,
  PlanTitle,
  PlanSubtitle,
  HorizontalLine,
  SpanPremium
} from '../styled';
import {StyledButton} from '../../Bots/styled';

const TokenPackageCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: 0.3s ease-in-out;
  background-color: transparent;
  color: ${({ theme }) => theme.color.text || '#202638'};

  &:hover {
    background-color: #ddd;
    color: ${({ theme }) => theme.color.background || '#E5E5E5'};
  }
`;

const AnimatedContent = styled.div`
  animation: fadeInSlide 0.3s ease-in-out;
  
  @keyframes fadeInSlide {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AddTokensButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: #179544;
  border: none;
  border-radius: 4px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-left: 15px;

  &:hover {
    background-color: #1db954;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const TokenInfoContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const TokenValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.spotifyWhite};
`;

const PlanBadge = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-right: 12px;
  font-family: 'CircularSp', sans-serif;
  color: #FFFFFF; /* Domyślny kolor dla Standard */

  ${({ isPremium }) =>
    isPremium &&
    css`
      background: linear-gradient(90deg, #ffdb4d 0%, #ff8c00 100%);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    `}
`;

const TransferForm = styled.div`
 background: #f5f5f5; 
            padding: 20px;
            border-radius: 8px; 
            margin-top: 15px;
            border: 1px solid #ddd;
  color: ${({ theme }) => theme.color.spotifyBlack};
`;

const InfoContent = () => (
  <>
    <ContentHeader>Informacje o tokenach</ContentHeader>
    <ContentSection>
      <SectionTitle>Co to są tokeny?</SectionTitle>
      <SectionDescription>
        Tokeny to jednostki używane do generowania treści w aplikacji. Każda operacja AI kosztuje określoną liczbę tokenów w zależności od złożoności zadania.
      </SectionDescription>
      <ContentSection>
        <SectionTitle>Jak działają tokeny?</SectionTitle>
        <SectionDescription>
          • Generowanie tekstu: 1-10 tokenów na operację*<br/>
          • Treści internetowe: 12-15 tokenów na operację*<br/>
          • Generowanie obrazów: 12-30 tokenów na obraz*<br/>
          • Generowanie muzyki: 40 tokenów na utwór<br/>
          • Generowanie wideo: 50-150 tokenów na film*<br/>
          <p style={{ fontSize: "10px", marginTop: "10px" }} >* w zależności od złożoności operacji</p>
        </SectionDescription>
      </ContentSection>
      <ContentSection>
        <SectionTitle>Limity tokenów</SectionTitle>
        <SectionDescription>
          • Plan Standard: 3500 tokenów miesięcznie, odnawialne każdego miesiąca (Tokeny nie sumują się)<br/>
          • Plan Premium: 7000 tokenów miesięcznie, odnawialne każdego miesiąca (Tokeny nie sumują się)<br/>
          • Dodatkowe tokeny można dokupić w dowolnym momencie
        </SectionDescription>
      </ContentSection>
    </ContentSection>
  </>
);

const AddTokensContent = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const activeSubscription = useSelector(selectActiveSubscription);

  const handlePackageSelect = (tokenAmount) => {
    setSelectedPackage(tokenAmount);
  };

  const handleBackToPackages = () => {
    setSelectedPackage(null);
  };

  // Pobierz email użytkownika
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email) {
      setUserEmail(currentUser.email);
    }
  }, []);

  const getPackageInfo = (tokenAmount) => {
    const email = userEmail || 'email'; // fallback jeśli email nie jest dostępny
    switch (tokenAmount) {
      case 50:
        return {
          tokens: 180,
          price: '9,99',
          title: `${email}_180`
        };
      case 200:
        return {
          tokens: 700,
          price: '29,99',
          title: `${email}_700`
        };
      case 500:
        return {
          tokens: 1500,
          price: '59,99',
          title: `${email}_1500`
        };
      default:
        return null;
    }
  };

  return (
    <div key={selectedPackage || 'default'}>
      {selectedPackage ? (
        // Instrukcja płatności dla wybranego pakietu
        <>
          <ContentHeader>Instrukcja płatności</ContentHeader>
          <ContentSection>
            <SectionTitle>Przelew BLIK</SectionTitle>
            <SectionDescription>
              Aby dokupić {getPackageInfo(selectedPackage).tokens} tokenów, wykonaj przelew BLIK na telefon, zgodnie z poniższymi danymi:
            </SectionDescription>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>Dane do przelewu</SectionTitle>
            <TransferForm>
              <div style={{ marginBottom: '10px' }}>
                <strong>Numer telefonu:</strong> +48 512058020
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Tytuł przelewu:</strong> {getPackageInfo(selectedPackage).title}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Kwota:</strong> {getPackageInfo(selectedPackage).price} PLN
              </div>
            </TransferForm>
            <SectionDescription/>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>Co dalej?</SectionTitle>
            <SectionDescription>
              Po wykonaniu przelewu, tokeny zostaną dodane do Twojego konta w ciągu 24 godzin. 
              Możesz sprawdzić stan swoich tokenów w zakładce "Twoje tokeny".
            </SectionDescription>
          </ContentSection>
            <ContentSection>
            <SectionTitle>Inne metody płatności</SectionTitle>
            <SectionDescription>
             Jeżeli wolisz inną metodę płatności, potrzebujesz indywidualnie dobranej ilości tokenów, lub masz pytania dotyczące zakupu tokenów, skontaktuj się z nami przez chat. 
            </SectionDescription>
          </ContentSection>
          
          <div style={{ marginTop: '20px' }}>
            <StyledButton onClick={handleBackToPackages}>
              ← Wróć do pakietów
            </StyledButton>
          </div>
        </>
      ) : (
        // Domyślny widok z kafelkami cenowymi
        <>
          <ContentHeader>Dodaj tokeny</ContentHeader>
          <ContentSection>
            <SectionTitle>Wybierz pakiet tokenów</SectionTitle>
            <SectionDescription>
              Dokup dodatkowe tokeny, aby móc korzystać z większej liczby operacji AI.
            </SectionDescription>
          </ContentSection>
          
          <ContentSection>
            <SectionTitle>Dostępne pakiety</SectionTitle>
            <SectionDescription>
              Wybierz pakiet, który najlepiej odpowiada Twoim potrzebom:
            </SectionDescription>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
              <TokenPackageCard onClick={() => handlePackageSelect(50)}>
                <SectionTitle>180 tokenów - 9,99 PLN</SectionTitle>
                <SectionDescription>
                  Idealne dla okazjonalnego użytkowania
                </SectionDescription>
              </TokenPackageCard>
              
              <TokenPackageCard onClick={() => handlePackageSelect(200)}>
                <SectionTitle>700 tokenów - 29,99 PLN</SectionTitle>
                <SectionDescription>
                  Najlepszy stosunek ceny do ilości
                </SectionDescription>
              </TokenPackageCard>
              
              <TokenPackageCard onClick={() => handlePackageSelect(500)}>
                <SectionTitle>1500 tokenów - 59,99 PLN</SectionTitle>
                <SectionDescription>
                  Dla intensywnego użytkowania
                </SectionDescription>
              </TokenPackageCard>
            </div>
          </ContentSection>
        </>
      )}
    </div>
  );
};

const YourTokensContent = () => {
  const openAiTokens = useSelector(selectOpenAiTokens);
  const activeSubscription = useSelector(selectActiveSubscription);
  const navigate = useNavigate();

  const getPlanName = () => {
    if (!activeSubscription || !activeSubscription.priceId) {
      return { name: "Brak planu", isPremium: false };
    }
    const standardPlans = [
      process.env.REACT_APP_STANDARD_MONTHLY,
      process.env.REACT_APP_STANDARD_YEARLY,
    ];
    const premiumPlans = [
      process.env.REACT_APP_PREMIUM_MONTHLY,
      process.env.REACT_APP_PREMIUM_YEARLY,
    ];

    if (standardPlans.includes(activeSubscription.priceId)) {
      return { name: "Standard", isPremium: false };
    }
    if (premiumPlans.includes(activeSubscription.priceId)) {
      return { name: "Premium", isPremium: true };
    }
    return { name: "Nieznany plan", isPremium: false };
  };

  const planInfo = getPlanName();

  return (
    <>
      <ContentHeader>Twoje tokeny</ContentHeader>
      <ContentSection>
        <SectionTitle>Stan Twoich tokenów</SectionTitle>
        <SectionDescription>
          Sprawdź ile tokenów pozostało w Twoim aktualnym planie.
        </SectionDescription>
        
        <TokenInfoContainer>
          <PlanBadge isPremium={planInfo.isPremium}>
            {planInfo.name}
          </PlanBadge>
          <TokenValue>{openAiTokens || 0}</TokenValue>
          <AddTokensButton onClick={() => navigate('/tokens/add')}>
            <FiPlus />
          </AddTokensButton>
        </TokenInfoContainer>
      </ContentSection>
    </>
  );
};



const Tokens = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [initialLoading, setInitialLoading] = useState(true);
  const isLoading = useSelector(selectIsLoading);
  const user = useSelector(selectUser);
  const tilesDetails = useSelector(selectTilesDetails);
  const restrictedDataError = useSelector(selectRestrictedDataError);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          dispatch(fetchData());
          const ref = doc(db, 'Users', currentUser.uid);
          await getDoc(ref);
          await delay(600);
        } catch (error) {
          console.error('Błąd pobierania danych użytkownika:', error);
          await delay(600);
        }
      } else {
        navigate('/login');
      }
      setInitialLoading(false);
    });
    return () => unsubscribe();
  }, [dispatch, navigate]);

  useEffect(() => {
    if (restrictedDataError) {
      navigate('/unauthorized');
    }
  }, [restrictedDataError, navigate]);

  const menuItems = [
    {
      path: 'your-tokens',
      name: 'Twoje tokeny',
      component: <YourTokensContent />,
      icon: <FiUser />,
    },
    {
      path: 'info',
      name: 'Informacje',
      component: <InfoContent />,
      icon: <FiInfo />,
    },
    {
      path: 'add',
      name: 'Dodaj tokeny',
      component: <AddTokensContent />,
      icon: <FiPlus />,
    },
  ];

  if (initialLoading || (isLoading === 'loading' && tilesDetails.length === 0)) {
    return <LoadingPage />;
  }

  if (restrictedDataError) {
    return <ErrorPage />;
  }

  if (!user) {
    return <LoadingPage isHighZIndex={true} />;
  }

  if (tilesDetails.length > 0) {
    return (
      <SettingsWrapper>
        <LeftMenu>
          {menuItems.map((item) => (
            <MenuItem key={item.path} to={item.path}>
              {item.icon}
              <span>{item.name}</span>
            </MenuItem>
          ))}
        </LeftMenu>
        <ContentArea>
          <Routes>
            <Route index element={<Navigate to={menuItems[0].path} replace />} />
            {menuItems.map((item) => (
              <Route key={item.path} path={item.path} element={item.component} />
            ))}
          </Routes>
        </ContentArea>
      </SettingsWrapper>
    );
  } else if (isLoading === 'success') {
    // Jeśli ładowanie zakończone, a danych wciąż nie ma
    return handleUnauthorizedAccess();
  }

  // Domyślnie pokazuj stronę ładowania, jeśli żadne inne warunki nie są spełnione
  return <LoadingPage />;
};

export default Tokens; 